import { is403Error, isAxiosError, parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';

const REDIRECT_URL_KEYS = [
  'checkoutSessionUrl',
  'checkout_session_url',
  'portalUrl',
  'portal_url',
  // Prefer named Stripe fields above. Generic `url` is last-resort only.
  'url',
] as const;

/** Stripe checkout / billing portal hosts only — reject open redirects. */
const ALLOWED_REDIRECT_HOSTS = new Set([
  'checkout.stripe.com',
  'billing.stripe.com',
  // Some Checkout / Payment Element flows use pay.stripe.com.
  'pay.stripe.com',
]);

const isAxiosResponseLike = (
  payload: unknown
): payload is { data: unknown; status: number } =>
  !!payload &&
  typeof payload === 'object' &&
  'data' in payload &&
  'status' in payload &&
  typeof (payload as { status: unknown }).status === 'number';

const unwrapRedirectPayload = (payload: unknown): unknown => {
  // Only unwrap real Axios responses. Never treat a body that happens to have
  // a `data` field (or axios `config.url`) as the redirect source.
  if (isAxiosResponseLike(payload)) {
    return payload.data;
  }
  return payload;
};

const findRedirectCandidate = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const record = payload as Record<string, unknown>;
  for (const key of REDIRECT_URL_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
};

/**
 * Only allow absolute HTTPS redirects to approved Stripe hosts. Relative paths
 * (e.g. `/v1/...`) would navigate the SPA to nginx and surface a raw 404 HTML
 * page; HTTP and non-Stripe hosts are rejected as open-redirect risks.
 */
export const extractExternalRedirectUrl = (
  payload: unknown
): string | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const record = payload as Record<string, unknown>;
  for (const key of REDIRECT_URL_KEYS) {
    const value = record[key];
    if (typeof value !== 'string' || !value.trim()) {
      continue;
    }
    try {
      const url = new URL(value.trim());
      if (
        url.protocol === 'https:' &&
        ALLOWED_REDIRECT_HOSTS.has(url.hostname)
      ) {
        return url.toString();
      }
    } catch {
      // not an absolute URL
    }
  }

  return undefined;
};

export const redirectToExternalUrl = (
  payload: unknown,
  missingMessage: string
): void => {
  const body = unwrapRedirectPayload(payload);
  const url = extractExternalRedirectUrl(body);
  if (!url) {
    const candidate = findRedirectCandidate(body);
    notify.error(
      candidate
        ? `${missingMessage} Rejected redirect: ${candidate.slice(0, 160)}`
        : missingMessage
    );
    return;
  }

  window.location.assign(url);
};

export const notifyReportingMonetizationApiError = (
  error: unknown,
  action: 'checkout' | 'billing portal' | 'change plan' | 'confirm checkout'
): void => {
  if (is403Error(error)) {
    notify.error(
      `Unable to start reporting ${action}. Please try again or contact support if the problem continues.`
    );
    return;
  }

  if (isAxiosError(error) && error.response?.status === 404) {
    notify.error(
      `Reporting ${action} is temporarily unavailable. Please try again later.`
    );
    return;
  }

  notify.error(parseApiError(error));
};
